import React, {
   CSSProperties,
   forwardRef,
   ReactElement,
   ReactNode,
   Ref,
   useEffect,
   useImperativeHandle,
   useState
} from 'react';
import { CardPanel, CardPanelItem, CardPanelItemProps } from '../cardpanel';
import { childrenOfType, findChildOfType } from '../../../utils';

/**
 * {@docCategory StackPanel}
 */
export interface StackPanelChangeEvent {
   oldCardId: string | undefined;
   oldCard: ReactElement | undefined;
   newCardId: string;
   newCard: ReactElement;
   backEmpty: boolean;
   forwardEmpty: boolean;
}

/**
 * {@docCategory StackPanel}
 */
export interface StackPanelProps {
   /**
    * Determines if the panel has a border around it.
    *
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;

   /**
    * Called when the active view changes.
    * @param event - The stack panel change event object
    */
   onChange?: (event: StackPanelChangeEvent) => void;
}

/**
 * {@docCategory StackPanel}
 */
export interface StackPanelRefType {
   back: () => void;
   forward: () => void;
   go: (id: string) => void;
   activeCard: () => ReactElement | undefined;
}

export declare type StackPanelPropsWithChildren = React.PropsWithChildren<StackPanelProps>;

export const StackPanel = forwardRef<StackPanelRefType | undefined, StackPanelPropsWithChildren>(
   (props: StackPanelPropsWithChildren, ref: Ref<StackPanelRefType | undefined>) => {
      const [backStack] = useState<string[]>([]);
      const [forwardStack] = useState<string[]>([]);
      const [activeCardId, setActiveCardId] = useState<string>();

      useEffect(() => {
         if (activeCardId === undefined) {
            const initialActiveCardId = getIdOfFirstCard(props.children);

            if (initialActiveCardId) {
               setActiveCardId(initialActiveCardId);

               if (props.onChange) {
                  props.onChange({
                                    oldCardId: undefined,
                                    oldCard: undefined,
                                    newCardId: initialActiveCardId,
                                    newCard: getCardWithId(props.children, initialActiveCardId),
                                    backEmpty: backStack.length === 0,
                                    forwardEmpty: forwardStack.length === 0
                                 })
               }
            }
         }
      }, [activeCardId]);

      useImperativeHandle(ref, () => ({
         back: () => {
            const newActiveCardId = backStack.pop();

            if (newActiveCardId) {
               if (activeCardId) {
                  forwardStack.push(activeCardId);
               }

               setActiveCardId(newActiveCardId);

               if (props.onChange) {
                  props.onChange({
                                    oldCardId: activeCardId,
                                    oldCard: activeCardId === undefined ? undefined : getCardWithId(props.children, activeCardId),
                                    newCardId: newActiveCardId,
                                    newCard: getCardWithId(props.children, newActiveCardId),
                                    backEmpty: backStack.length === 0,
                                    forwardEmpty: forwardStack.length === 0
                                 })
               }
            }
         },
         forward: () => {
            const newActiveCardId = forwardStack.pop();

            if (newActiveCardId) {
               if (activeCardId) {
                  backStack.push(activeCardId);
               }

               setActiveCardId(newActiveCardId);

               if (props.onChange) {
                  props.onChange({
                                    oldCardId: activeCardId,
                                    oldCard: activeCardId === undefined ? undefined : getCardWithId(props.children, activeCardId),
                                    newCardId: newActiveCardId,
                                    newCard: getCardWithId(props.children, newActiveCardId),
                                    backEmpty: backStack.length === 0,
                                    forwardEmpty: forwardStack.length === 0
                                 })
               }
            }
         },
         go: (id: string) => {
            if (activeCardId) {
               backStack.push(activeCardId);
            }

            setActiveCardId(id);

            if (props.onChange) {
               props.onChange({
                                 oldCardId: activeCardId,
                                 oldCard: activeCardId === undefined ? undefined : getCardWithId(props.children, activeCardId),
                                 newCardId: id,
                                 newCard: getCardWithId(props.children, id),
                                 backEmpty: backStack.length === 0,
                                 forwardEmpty: forwardStack.length === 0
                              })
            }
         },
         activeCard: () => {
            return activeCardId === undefined ? undefined : getCardWithId(props.children, activeCardId);
         }
      } as StackPanelRefType));

      return (
         <CardPanel
            activeCardId={activeCardId}
            border={props.border}
            className={props.className}
            style={props.style}
         >
            {props.children}
         </CardPanel>
      );
   });

const getCardWithId = (children: ReactNode, id: string): ReactElement<CardPanelItemProps> => {
   const card = findChildOfType<CardPanelItemProps>(children, CardPanelItem, child => {
      return child.props.id === id;
   });

   if (card) {
      return card;
   }

   throw Error(`Unable to locate a CardPanelItem with id ${id}`);
};

const getIdOfFirstCard = (children: ReactNode): string | undefined => {
   const childArray = childrenOfType<CardPanelItemProps>(children, CardPanelItem);

   if (childArray.length > 0) {
      return childArray[0].props.id;
   }

   return undefined;
};
